import type { TabelaPreco } from '@/features/tabela-preco/types/TabelaPreco';
import type { TabelaPrecoProdutoExcecao } from '@/features/tabela-preco/types/TabelaPrecoProduto';
import {
    TabelaPrecoExcecaoTipoPreco,
    TabelaPrecoTipoVariacao,
} from '@/features/tabela-preco/types/enum';
import { numberToCurrency } from '@/std/format';
import { roundAbnt } from '@/std/math';
import { unreachable } from '@/std/util';
import type { Produto } from '../types/Produto';
import { type ProdutoPacoteBarra, ProdutoPacoteBarraTipoPreco } from '../types/ProdutoPacoteBarra';

function calcularExcecao(excecao: TabelaPrecoProdutoExcecao, preco: number): number {
    const tpe_preco = excecao.tpe_preco ?? 0;

    switch (excecao.tpe_tipo_preco) {
        case TabelaPrecoExcecaoTipoPreco.PrecoEspecificado: {
            return tpe_preco;
        }
        case TabelaPrecoExcecaoTipoPreco.PrecoProdutoOuPacoteBarras: {
            return preco;
        }
        case TabelaPrecoExcecaoTipoPreco.PercentualAcrescimo: {
            return roundAbnt(preco + (preco * tpe_preco) / 100, 2);
        }
        case TabelaPrecoExcecaoTipoPreco.PercentualDesconto: {
            return roundAbnt(preco - (preco * tpe_preco) / 100, 2);
        }
        case TabelaPrecoExcecaoTipoPreco.ValorAcrescimo: {
            return preco + tpe_preco;
        }
        case TabelaPrecoExcecaoTipoPreco.ValorDesconto: {
            return preco - tpe_preco;
        }
        default:
            return preco;
    }
}

function calcularPrecoPacoteBarra(
    produto: Produto,
    pacoteBarra: ProdutoPacoteBarra,
    tabelaPreco: TabelaPreco | null,
): number {
    const preco = calcularPrecoVendaProdutoPacote({
        pcb_multiplicador: pacoteBarra.pcb_multiplicador ?? 0,
        pcb_tipo_preco: pacoteBarra.pcb_tipo_preco,
        pcb_valor: pacoteBarra.pcb_valor ?? 0,
        pro_preco_venda: produto.pro_preco_venda ?? 0,
    }).preco;

    if (tabelaPreco) {
        const excecao = tabelaPreco.tabela_preco_excecao?.find(
            (e) =>
                e.tpe_produto_idpk === produto.pro_idpk &&
                e.tpe_produto_pacote_barra_idpk === pacoteBarra.pcb_idpk,
        );

        if (excecao) {
            return calcularExcecao(excecao, preco);
        }

        const percentual = tabelaPreco.tap_percentual ?? 0;

        if (tabelaPreco.tap_tipo_variacao === TabelaPrecoTipoVariacao.Acrescimo) {
            return roundAbnt(preco + (preco * percentual) / 100, 2);
        }

        if (tabelaPreco.tap_tipo_variacao === TabelaPrecoTipoVariacao.Desconto) {
            return roundAbnt(preco - (preco * percentual) / 100, 2);
        }
    }

    return preco;
}

export function calcularPrecoVendaProduto({
    produto,
    pacoteBarra,
    tabelaPreco,
}: {
    produto: Produto;
    pacoteBarra: ProdutoPacoteBarra | null;
    tabelaPreco: TabelaPreco | null;
}): number {
    const minimo = produto.pro_preco_minimo ?? 0;

    if (pacoteBarra) {
        return calcularPrecoPacoteBarra(produto, pacoteBarra, tabelaPreco);
    }

    // Utiliza a tabela de preço calculada, se houver
    if (produto.tabela_preco?.preco_calculado) {
        return Math.max(produto.tabela_preco.preco_calculado, minimo);
    }

    const precoVenda = produto.pro_preco_venda ?? 0;

    // Se não tiver a tabela de preço calculada utiliza a tabela de preço informada manualmente, se houver
    if (tabelaPreco) {
        const excecao = tabelaPreco.tabela_preco_excecao?.find(
            (e) =>
                e.tpe_produto_idpk === produto.pro_idpk && e.tpe_produto_pacote_barra_idpk === null,
        );

        if (excecao) {
            return Math.max(calcularExcecao(excecao, precoVenda), minimo);
        }

        const percentual = tabelaPreco.tap_percentual ?? 0;

        if (tabelaPreco.tap_tipo_variacao === TabelaPrecoTipoVariacao.Acrescimo) {
            return Math.max(roundAbnt(precoVenda + (precoVenda * percentual) / 100, 2), minimo);
        }

        if (tabelaPreco.tap_tipo_variacao === TabelaPrecoTipoVariacao.Desconto) {
            return Math.max(roundAbnt(precoVenda - (precoVenda * percentual) / 100, 2), minimo);
        }
    }

    return Math.max(precoVenda, minimo);
}

export function calcularPrecoVendaProdutoPacote({
    pcb_multiplicador,
    pcb_valor,
    pcb_tipo_preco,
    pro_preco_venda,
}: {
    pcb_multiplicador: number;
    pcb_valor: number;
    pcb_tipo_preco: ProdutoPacoteBarraTipoPreco | undefined | null;
    pro_preco_venda: number;
}): { preco: number; description: string } {
    if (!pcb_tipo_preco) {
        return { description: '', preco: 0 };
    }

    const porcentagem = (pcb_valor / 100) * pro_preco_venda;

    switch (pcb_tipo_preco) {
        case ProdutoPacoteBarraTipoPreco.IgualPrecoCadastro: {
            return {
                description: 'Igual ao preço do cadastro',
                preco: roundAbnt(pro_preco_venda * pcb_multiplicador, 2),
            };
        }
        case ProdutoPacoteBarraTipoPreco.AcrescentarPrecoCadastro: {
            const precoVendaProdutoFormatted = numberToCurrency(pro_preco_venda);
            const preco = roundAbnt((pro_preco_venda + porcentagem) * pcb_multiplicador, 2);

            return {
                description: `(${precoVendaProdutoFormatted} + ${pcb_valor}%) x ${pcb_multiplicador} = ${numberToCurrency(preco)}`,
                preco,
            };
        }
        case ProdutoPacoteBarraTipoPreco.DescontarPrecoCadastro: {
            const precoVendaProdutoFormatted = numberToCurrency(pro_preco_venda);
            const preco = roundAbnt((pro_preco_venda - porcentagem) * pcb_multiplicador, 2);

            return {
                description: `(${precoVendaProdutoFormatted} - ${pcb_valor}%) x ${pcb_multiplicador} = ${numberToCurrency(preco)}`,
                preco,
            };
        }
        case ProdutoPacoteBarraTipoPreco.PrecoPersonalizado: {
            return {
                description: 'Preço personalizado',
                preco: roundAbnt(pcb_valor, 2),
            };
        }
        default: {
            unreachable(pcb_tipo_preco);
        }
    }
}
